import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { listPlugins, togglePlugin, type PluginConfig } from "@/api/plugins";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";
import {
  countEnabledPlugins,
  filterPlugins,
  type PluginStatusFilter,
} from "@/lib/plugin-ui";

const ALLOWED_MODES = ["cards", "list", "table"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "status", order: "desc", label: "Enabled first" },
];

function applySort(items: PluginConfig[], sort: EntitySortOption): PluginConfig[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "status") {
      return (Number(a.enabled) - Number(b.enabled)) * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

export function SettingsPluginsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<PluginStatusFilter>("all");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_settings_plugins",
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const useServerCursor = !searchQuery.trim() && statusFilter === "all";

  const cursorPag = useCursorPagination([]);

  const {
    data: cursorData,
    isLoading: cursorLoading,
    error: cursorError,
  } = useQuery({
    queryKey: ["plugins", "cursor", cursorPag.pageSize, cursorPag.cursor],
    queryFn: () =>
      listPlugins({
        limit: cursorPag.pageSize,
        cursor: cursorPag.cursor,
      }),
    enabled: useServerCursor,
    staleTime: 30_000,
  });

  useSyncCursorPage(
    cursorPag.setNextCursor,
    cursorData,
    useServerCursor && !cursorLoading && !!cursorData,
  );

  const {
    data: allData,
    isLoading: allLoading,
    error: allError,
  } = useQuery({
    queryKey: ["plugins", "all"],
    queryFn: async () => (await listPlugins()).plugins,
    enabled: !useServerCursor,
    staleTime: 30_000,
  });

  const isLoading = useServerCursor ? cursorLoading : allLoading;
  const error = useServerCursor ? cursorError : allError;

  const plugins = useServerCursor
    ? (cursorData?.plugins ?? [])
    : (allData ?? []);

  const filteredPlugins = useMemo(() => {
    if (useServerCursor) {
      return applySort(plugins, sortOption);
    }
    return applySort(
      filterPlugins(plugins, {
        search: searchQuery,
        status: statusFilter,
      }),
      sortOption,
    );
  }, [useServerCursor, plugins, searchQuery, statusFilter, sortOption]);

  const enabledCount = countEnabledPlugins(plugins);

  const clientPag = useClientPagination(
    useServerCursor ? [] : filteredPlugins,
    [searchQuery, statusFilter, sortOption.field],
  );

  const pageItems = useServerCursor ? filteredPlugins : clientPag.pageItems;

  const toggleMutation = useMutation({
    mutationFn: togglePlugin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plugins"] }),
  });

  const getSortValue = useCallback((p: PluginConfig, key: string) => {
    if (key === "status") return p.enabled ? 1 : 0;
    if (key === "version") return p.version;
    return p.name;
  }, []);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems.map((p) => ({ ...p, id: p.pluginId })),
      defaultKey: null,
      getValue: getSortValue,
    });

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "status",
      label: "Status",
      value: statusFilter === "all" ? "" : statusFilter,
      allLabel: "All status",
      options: [
        { value: "enabled", label: "Enabled" },
        { value: "disabled", label: "Disabled" },
      ],
      onChange: (v) => setStatusFilter((v || "all") as PluginStatusFilter),
    },
  ];

  const toggleControl = (plugin: PluginConfig) => (
    <label
      className="relative inline-flex cursor-pointer items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={plugin.enabled}
        onChange={() => toggleMutation.mutate(plugin.pluginId)}
        className="peer sr-only"
      />
      <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
      <span className="ml-2 text-xs text-muted-foreground">
        {plugin.enabled ? "Enabled" : "Disabled"}
      </span>
    </label>
  );

  const columns: Column<PluginConfig & { id: string }>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (p) => (
        <span className="font-medium text-foreground">{p.name}</span>
      ),
    },
    {
      key: "version",
      header: "Version",
      sortable: true,
      render: (p) => (
        <span className="text-muted-foreground">v{p.version}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (p) => toggleControl(p),
    },
  ];

  const catalogEmpty = !isLoading && !error && plugins.length === 0;
  const showNoMatch =
    !isLoading &&
    !error &&
    plugins.length > 0 &&
    filteredPlugins.length === 0;

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Plugins</h1>
        <p className="text-muted-foreground">
          App-wide plugin features. Toggle plugins on or off for all users.
        </p>
      </header>

      {!isLoading && !error && (useServerCursor || plugins.length > 0) && (
        <div>
          <CollectionCommandBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name, description, or ID…"
            sortOption={sortOption}
            onSortChange={setSortOption}
            sortOptions={SORT_OPTIONS}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowedModes={allowedModes}
            filterFields={filterFields}
            activeFilterCount={statusFilter !== "all" ? 1 : 0}
            onClear={() => {
              setSearchQuery("");
              setStatusFilter("all");
            }}
          />
          {!useServerCursor && (
            <p className="mt-2 text-xs text-muted-foreground">
              {filteredPlugins.length === 0
                ? `0 of ${plugins.length} (${enabledCount} enabled)`
                : `Showing ${clientPag.showingFrom}–${clientPag.showingTo} of ${filteredPlugins.length} (${enabledCount} enabled)`}
            </p>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load plugins.</p>}

      {catalogEmpty && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No plugins configured.</p>
        </div>
      )}

      {showNoMatch && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No plugins match your filters.
          </p>
        </div>
      )}

      {!isLoading && !error && pageItems.length > 0 && (
        <>
          <EntityListLayout
            viewMode={viewMode}
            cards={
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((plugin) => (
                  <EntityCard
                    key={plugin.pluginId}
                    id={plugin.pluginId}
                    title={plugin.name}
                    description={plugin.description || plugin.pluginId}
                    primaryBadge={{
                      label: `v${plugin.version}`,
                      colorClass: "bg-muted text-muted-foreground",
                    }}
                    className={plugin.enabled ? "" : "opacity-60"}
                    footerContent={toggleControl(plugin)}
                    actions={
                      <Link
                        to={`/settings/plugins/${plugin.pluginId}`}
                        className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Details
                      </Link>
                    }
                    onClick={() =>
                      navigate(`/settings/plugins/${plugin.pluginId}`)
                    }
                  />
                ))}
              </div>
            }
            list={
              <div className="space-y-2">
                {pageItems.map((plugin) => (
                  <EntityCompactItem
                    key={plugin.pluginId}
                    id={plugin.pluginId}
                    title={plugin.name}
                    description={plugin.description || plugin.pluginId}
                    badges={[
                      {
                        label: `v${plugin.version}`,
                        colorClass: "bg-muted text-muted-foreground",
                      },
                    ]}
                    className={plugin.enabled ? "" : "opacity-60"}
                    actions={
                      <>
                        {toggleControl(plugin)}
                        <Link
                          to={`/settings/plugins/${plugin.pluginId}`}
                          className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Details
                        </Link>
                      </>
                    }
                    onClick={() =>
                      navigate(`/settings/plugins/${plugin.pluginId}`)
                    }
                  />
                ))}
              </div>
            }
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(p) => navigate(`/settings/plugins/${p.pluginId}`)}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            }
          />
          {useServerCursor ? (
            <PaginationBar
              mode="cursor"
              page={cursorPag.page}
              pageSize={cursorPag.pageSize}
              itemCount={pageItems.length}
              showingFrom={
                pageItems.length === 0 ? 0 : cursorPag.showingFrom
              }
              showingTo={cursorPag.showingTo(pageItems.length)}
              hasMore={cursorPag.hasMore}
              canPrev={cursorPag.canPrev}
              onFirst={cursorPag.goFirst}
              onPrev={cursorPag.goPrev}
              onNext={cursorPag.goNext}
              onPageSizeChange={cursorPag.setPageSize}
            />
          ) : (
            <PaginationBar
              page={clientPag.page}
              pageSize={clientPag.pageSize}
              totalItems={filteredPlugins.length}
              totalPages={clientPag.totalPages}
              showingFrom={clientPag.showingFrom}
              showingTo={clientPag.showingTo}
              onPageChange={clientPag.setPage}
              onPageSizeChange={clientPag.setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
